import type { TFunction } from "i18next";

import type {
  FiscalAgendaObligation,
  FiscalObligationAuthority,
  FiscalTaxType,
} from "@/services/fiscalService";
import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";

const TAX_LABEL_KEYS: Record<FiscalTaxType, string> = {
  IVA: "fiscalAgenda.tax.iva",
  IRAE: "fiscalAgenda.tax.irae",
  BPS: "fiscalAgenda.tax.bps",
  IRPF: "fiscalAgenda.tax.irpf",
  IRNR: "fiscalAgenda.tax.irnr",
  MONOTRIBUTO: "fiscalAgenda.tax.monotributo",
  FONASA: "fiscalAgenda.tax.fonasa",
  FONASA_ANUAL: "fiscalAgenda.tax.fonasaAnual",
};

const AUTHORITY_LABEL_KEYS: Record<FiscalObligationAuthority, string> = {
  DGI: "fiscalAgenda.authority.dgi",
  BPS: "fiscalAgenda.authority.bps",
};

export type FiscalAgendaObligationStatus =
  | "upcoming"
  | "due_today"
  | "dueSoon"
  | "overdue"
  | "completed";

export function getTaxLabelKey(taxType: FiscalTaxType): string {
  return TAX_LABEL_KEYS[taxType] ?? "fiscalAgenda.tax.iva";
}

export function getAuthorityLabelKey(authority: FiscalObligationAuthority): string {
  return AUTHORITY_LABEL_KEYS[authority] ?? "fiscalAgenda.authority.dgi";
}

/**
 * Format declaration period (YYYY-MM) as localized month + year.
 */
export function formatPeriodForDisplay(period: string, locale: string): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return period;
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });
}

/** Short label for dashboard alerts: tax (authority) · period. */
export function formatObligationAlertTitle(
  obligation: FiscalAgendaObligation,
  t: TFunction,
  locale: string
): string {
  const tax = t(getTaxLabelKey(obligation.taxType));
  const authority = t(getAuthorityLabelKey(obligation.authority));
  const period = formatPeriodForDisplay(obligation.period, locale);
  return `${tax} (${authority}) · ${period}`;
}

export function getStatusBadgeVariant(
  status: FiscalAgendaObligationStatus
): StatusBadgeVariant {
  switch (status) {
    case "overdue":
    case "due_today":
      // Both demand action today — show red, not amber. Anything less
      // gets glossed over on a list of obligations.
      return "danger";
    case "dueSoon":
      return "warning";
    case "upcoming":
      return "neutral";
    case "completed":
      return "success";
    default:
      return "neutral";
  }
}
